/**
 * Config map for all dynamic category pages (kwiaty/:handle, okazje/:handle).
 * Each entry gives the hero text, images and filter settings for that handle.
 */
import type { CategoryPageConfig } from './categoryPageConfig';

import {
  img1, img2, img3,
  imgPastelowa, imgRozany, imgKompozycja, imgHero, imgRoze,
  imgFlowerBoxPremium as imgFlowerBoxP,
} from '@/data/categoryImages';

/* ─── Shared image sets ──────────────────────────────────── */
const ROSE_SET: CategoryPageConfig['heroImages'] = [
  { img: imgRoze,     pos: 'center',     label: 'Czerwone'    },
  { img: imgRozany,   pos: 'center top', label: 'Klasyczne'   },
  { img: imgFlowerBoxP, pos: 'center',   label: 'Premium'     },
  { img: img1,        pos: 'center top', label: 'Mieszane'    },
];
const PASTEL_SET: CategoryPageConfig['heroImages'] = [
  { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'   },
  { img: imgHero,      pos: 'center top',label: 'Eleganckie'  },
  { img: img1,         pos: 'center top',label: 'Klasyczne'   },
  { img: imgFlowerBoxP, pos: 'center',   label: 'Premium'     },
];
const SUNNY_SET: CategoryPageConfig['heroImages'] = [
  { img: img2,        pos: 'center',     label: 'Kolorowe'    },
  { img: imgKompozycja,pos:'center',     label: 'Sezonowe'    },
  { img: img1,        pos: 'center top', label: 'Klasyczne'   },
  { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'   },
];
const SEASONAL_SET: CategoryPageConfig['heroImages'] = [
  { img: imgKompozycja, pos: 'center',   label: 'Sezonowe'    },
  { img: img1,          pos: 'center top',label:'Klasyczne'   },
  { img: imgPastelowa,  pos: 'center',   label: 'Pastelowe'   },
  { img: img2,          pos: 'center',   label: 'Kolorowe'    },
];
const EXOTIC_SET: CategoryPageConfig['heroImages'] = [
  { img: img3,         pos: 'center top',label: 'Ekskluzywne' },
  { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'    },
  { img: img1,         pos: 'center top',label: 'Klasyczne'   },
  { img: imgHero,      pos: 'center top',label: 'Premium'     },
];
const ROMANTIC_SET: CategoryPageConfig['heroImages'] = [
  { img: imgRoze,      pos: 'center',    label: 'Róże'        },
  { img: imgRozany,    pos: 'center top',label: 'Klasyczne'   },
  { img: imgHero,      pos: 'center top',label: 'Eleganckie'  },
  { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'   },
];
const CELEBRATION_SET: CategoryPageConfig['heroImages'] = [
  { img: img1,         pos: 'center top',label: 'Bukiety'     },
  { img: img2,         pos: 'center',    label: 'Kolorowe'    },
  { img: imgKompozycja,pos: 'center',    label: 'Kompozycje'  },
  { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'   },
];

/* ─── Helpers ────────────────────────────────────────────── */
const ROSE  = '#9B2335';
const GOLD  = '#e05590';
const GREEN = '#4A7C59';

type Cfg = Omit<CategoryPageConfig, 'seoTitle' | 'seoDescription'> & {
  seoTitle?: string;
  seoDescription?: string;
};

const FLOWER_KOMPOZYCJE_OPTIONS = [
  { value: 'Bukiety',          label: 'Bukiety',          emoji: '💐' },
  { value: 'Flower Boxy',      label: 'Flower Boxy',      emoji: '🌸' },
  { value: 'Kwiaty w koszu',   label: 'Kwiaty w koszu',   emoji: '🧺' },
  { value: 'Kwiaty w torebce', label: 'Kwiaty w torebce', emoji: '👜' },
];
const FLOWER_KOMPOZYCJA_TAG_MAP: Record<string, string[]> = {
  'Bukiety':          ['bukiet'],
  'Flower Boxy':      ['flower box', 'flowerbox', 'flower-box'],
  'Kwiaty w koszu':   ['kosz', 'w koszu', 'koszu'],
  'Kwiaty w torebce': ['torebka', 'w torebce', 'torebce'],
};

function flower(
  handle: string, breadcrumb: string,
  h1Line1: string, h1Accent: string,
  description: string,
  heroImages: CategoryPageConfig['heroImages'],
  accentColor = ROSE,
): CategoryPageConfig {
  return {
    handle, breadcrumb,
    h1Line1, h1Accent, accentColor, description,
    heroImages,
    seoTitle: `${breadcrumb} — Lublin | Bukietowa`,
    seoDescription: `${breadcrumb} ze świeżych kwiatów z dostawą w Lublinie. Zamów online z Bukietowa.pl.`,
    visibleSections: ['kompozycje', 'okazje', 'cena'],
    kompozycjeLabel: 'Rodzaj kompozycji',
    kompozycjeOptions: FLOWER_KOMPOZYCJE_OPTIONS,
    kompozycjaTagMap: FLOWER_KOMPOZYCJA_TAG_MAP,
    countLabel: 'kompozycji w ofercie',
  };
}

const OKAZJA_KOMPOZYCJE_OPTIONS = [
  { value: 'Bukiety',            label: 'Bukiety',            emoji: '💐' },
  { value: 'Flower Boxy',        label: 'Flower Boxy',        emoji: '🌸' },
  { value: 'Kwiaty w koszu',     label: 'Kwiaty w koszu',     emoji: '🧺' },
  { value: 'Kwiaty w torebce',   label: 'Kwiaty w torebce',   emoji: '👜' },
  { value: 'Lasy w szkle',       label: 'Lasy w szkle',       emoji: '🌿' },
  { value: 'Balony z helem',     label: 'Balony z helem',     emoji: '🎈' },
  { value: 'Kwiaty doniczkowe',  label: 'Kwiaty doniczkowe',  emoji: '🪴' },
];
const OKAZJA_KOMPOZYCJA_TAG_MAP: Record<string, string[]> = {
  'Bukiety':           ['bukiet'],
  'Flower Boxy':       ['flower box', 'flowerbox', 'flower-box'],
  'Kwiaty w koszu':    ['kosz', 'w koszu', 'koszu'],
  'Kwiaty w torebce':  ['torebka', 'w torebce', 'torebce'],
  'Lasy w szkle':      ['las w szkle', 'lasy w szkle', 'terrarium'],
  'Balony z helem':    ['balon', 'balony', 'hel'],
  'Kwiaty doniczkowe': ['doniczka', 'doniczkowe', 'doniczkowy'],
};

function okazja(
  handle: string, breadcrumb: string,
  h1Line1: string, h1Accent: string,
  description: string,
  heroImages: CategoryPageConfig['heroImages'],
  accentColor = ROSE,
): CategoryPageConfig {
  return {
    handle, breadcrumb,
    h1Line1, h1Accent, accentColor, description,
    heroImages,
    seoTitle: `${breadcrumb} — kwiaty | Bukietowa Lublin`,
    seoDescription: `Piękne kwiaty na ${breadcrumb.toLowerCase()} z dostawą w Lublinie. Zamów online z Bukietowa.pl.`,
    visibleSections: ['kompozycje', 'kwiaty', 'cena'],
    kompozycjeLabel: 'Rodzaj kompozycji',
    kompozycjeOptions: OKAZJA_KOMPOZYCJE_OPTIONS,
    kompozycjaTagMap: OKAZJA_KOMPOZYCJA_TAG_MAP,
    countLabel: 'kompozycji w ofercie',
  };
}

/* ─── Config map ─────────────────────────────────────────── */
export const categoryConfigMap: Record<string, CategoryPageConfig> = {

  /* ── Kwiaty (flower types) ──────────────────────────────── */
  'roze': flower(
    'roze', 'Róże',
    'Róże i bukiety róż', 'z dostawą w Lublinie',
    'Klasyczne róże lub kompozycje z dodatkiem innych kwiatów. Porównaj kolorystykę, formę i dostępne warianty, żeby wybrać kwiaty odpowiednie do okazji i gustu odbiorcy.',
    ROSE_SET,
  ),

  'lilie': flower(
    'lilie', 'Lilie',
    "Lilie w bukietach", "i kompozycjach",
    "Szukasz kwiatów o wyrazistej formie? Porównaj kompozycje z liliami, zwracając uwagę na kolorystykę, pozostałe gatunki i wielkość wybranego wariantu.",
    PASTEL_SET,
  ),

  'gerbery': flower(
    'gerbery', 'Gerbery',
    "Bukiety z gerberami", "z dostawą w Lublinie",
    "Gerbery mogą nadać kompozycji wyraźny kolor lub uzupełnić mieszany bukiet. Wybierz zestawienie pasujące do urodzin, podziękowania czy codziennego prezentu.",
    SUNNY_SET,
    GOLD,
  ),

  'gozdziki': flower(
    'gozdziki', 'Goździki',
    "Goździki", "- bukiety i kompozycje na prezent",
    "Jednolite kolory czy kilka gatunków w jednym bukiecie? Znajdź kompozycję z goździkami dopasowaną do osoby i okazji, a szczegóły sprawdź przy wybranym produkcie.",
    SEASONAL_SET,
  ),

  'sloneczniki': flower(
    'sloneczniki', 'Słoneczniki',
    "Bukiety ze słonecznikami", "z dostawą w Lublinie",
    "Żółte kwiaty jako główny akcent prezentu. Obejrzyj bukiety i kompozycje ze słonecznikami, porównując ich formę oraz sposób, w jaki chcesz je wręczyć.",
    SUNNY_SET,
    GOLD,
  ),

  'alstroemerie': flower(
    'alstroemerie', 'Alstroemerie',
    "Kompozycje z alstroemeriami", "- kwiaty na prezent",
    "Alstroemerie możesz wybrać jako element kwiatowego prezentu. Porównaj dostępne zestawienia z innymi gatunkami i szukaj kolorów bliskich gustowi odbiorcy.",
    PASTEL_SET,
  ),

  'eustomy': flower(
    'eustomy', 'Eustomy',
    "Bukiety z eustomami", "z dostawą",
    "Wybierz eustomy w kompozycji odpowiadającej okazji: od spokojnego prezentu po wyrazisty bukiet mieszany. Kolorystykę i dostępne wielkości porównasz na kartach produktów.",
    PASTEL_SET,
  ),

  'gipsowka': flower(
    'gipsowka', 'Gipsówka',
    "Gipsówka w bukietach", "i kompozycjach kwiatowych",
    "Drobny akcent w mieszanym bukiecie czy ważny element całej kompozycji? Obejrzyj propozycje z gipsówką i sprawdź w opisie, jakie kwiaty jej towarzyszą.",
    [
      { img: img1,        pos: 'center top', label: 'Klasyczna'  },
      { img: imgHero,     pos: 'center top', label: 'Elegancka'  },
      { img: imgPastelowa,pos: 'center',     label: 'Pastelowa'  },
      { img: imgFlowerBoxP,pos:'center',     label: 'Premium'    },
    ],
  ),

  'margaretki': flower(
    'margaretki', 'Margaretki',
    "Bukiety z margaretkami", "na różne okazje",
    "Poszukaj kompozycji z margaretkami na spotkanie, urodziny albo drobne podziękowanie. Dopasuj kolory i wielkość bukietu do osoby, której chcesz go podarować.",
    SUNNY_SET,
  ),

  'tulipany': flower(
    'tulipany', 'Tulipany',
    'Tulipany', '- bukiety i kompozycje',
    'Sprawdź aktualną ofertę tulipanów. Jeśli nie widzisz dostępnego produktu, zapytaj o możliwość przygotowania bukietu na wybrany dzień i w preferowanej kolorystyce.',
    SEASONAL_SET,
  ),

  'piwonie': flower(
    'piwonie', 'Piwonie',
    "Piwonie", "- bukiety na wyjątkowe chwile",
    "Sprawdź aktualne propozycje z piwoniami. Gdy planujesz prezent na konkretny dzień, upewnij się, czy wybrana kompozycja i preferowany kolor są dostępne.",
    PASTEL_SET,
  ),

  'hortensje': flower(
    'hortensje', 'Hortensje',
    "Kompozycje z hortensjami", "i bukiety na prezent",
    "Hortensja może być mocnym akcentem kwiatowej kompozycji. Porównaj dostępne bukiety, ich kolorystykę i oprawę, aby dopasować prezent do planowanego spotkania.",
    [
      { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'  },
      { img: img1,         pos: 'center top',label: 'Białe'      },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: imgFlowerBoxP,pos: 'center',    label: 'Premium'    },
    ],
    '#7B8EC8',
  ),

  'lewkonie': flower(
    'lewkonie', 'Lewkonie',
    "Lewkonie", "w bukietach na prezent",
    "Obejrzyj aktualne kompozycje z lewkoniami. Wybierając kwiaty dla bliskiej osoby, uwzględnij jej ulubione barwy oraz preferencje dotyczące zapachu kwiatów.",
    PASTEL_SET,
  ),

  'frezje': flower(
    'frezje', 'Frezje',
    "Frezje", "- bukiety i kwiatowe upominki",
    "Szukasz kompozycji z frezjami? Sprawdź dostępne propozycje oraz ich skład i wybierz formę pasującą do spotkania, życzeń lub podziękowania.",
    PASTEL_SET,
  ),

  'jaskier': flower(
    'jaskier', 'Jaskry',
    "Bukiety z jaskrami", "i kompozycje kwiatowe",
    "Wybierz kompozycję z jaskrami, zwracając uwagę na paletę barw i towarzyszące kwiaty. Aktualną dostępność oceń na podstawie oferty, a nietypowe życzenia uzgodnij z kwiaciarnią.",
    SUNNY_SET,
    '#e05590',
  ),

  'anturium': flower(
    'anturium', 'Anturium',
    "Kompozycje z anturium", "- kwiaty o wyrazistej formie",
    "Poszukaj prezentu, w którym liczy się kształt całej kompozycji. Porównaj propozycje z anturium, ich kolorystykę i dodatki pasujące do gustu odbiorcy.",
    EXOTIC_SET,
    '#9B2335',
  ),

  'lwiepaszcze': flower(
    'lwiepaszcze', 'Lwie paszcze',
    "Bukiety z lwimi paszczami", "i kwiatowe kompozycje",
    "Lwie paszcze wybierz z myślą o formie bukietu i miejscu jego ustawienia. Przyjrzyj się proporcjom oraz zestawieniom z pozostałymi kwiatami w aktualnej ofercie.",
    SUNNY_SET,
  ),

  'dalie': flower(
    'dalie', 'Dalie',
    "Dalie", "- bukiety i kompozycje na prezent",
    "Sprawdź kompozycje z daliami i znajdź kolorystykę odpowiednią na prezent. Przy planowaniu zamówienia z wyprzedzeniem zapytaj o dostępność kwiatów na wybraną datę.",
    [
      { img: img2,         pos: 'center',    label: 'Pompony'    },
      { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'   },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
      { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'  },
    ],
  ),

  'kalia': flower(
    'kalia', 'Kalie',
    "Kalie w bukietach", "i eleganckich kompozycjach",
    "Porównaj kompozycje z kaliami i dopasuj ich formę do okazji. Sprawdź opis produktu, aby wybrać właściwy rodzaj prezentu lub kwiatów na uroczystość.",
    [
      { img: img1,         pos: 'center top',label: 'Białe'      },
      { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: imgFlowerBoxP,pos: 'center',    label: 'Premium'    },
    ],
  ),

  /* ── Okazje ──────────────────────────────────────────────── */
  'urodziny': okazja(
    'urodziny', 'Urodziny',
    "Bukiety urodzinowe", "z dostawą",
    "Wybierz kwiaty w ulubionych kolorach solenizanta. Porównaj bukiety i kompozycje na urodziny, a przed zamówieniem sprawdź termin dostawy dla adresu odbiorcy.",
    CELEBRATION_SET,
  ),

  'imieniny': okazja(
    'imieniny', 'Imieniny',
    "Kwiaty na imieniny", "z dostawą",
    "Bukiet do osobistego wręczenia albo kwiaty z życzeniami na odległość. Znajdź kompozycję, która pasuje do solenizanta i charakteru Waszej relacji.",
    CELEBRATION_SET,
  ),

  'rocznica': okazja(
    'rocznica', 'Rocznica',
    "Kwiaty na rocznicę", "z dostawą",
    "Nawiąż do wspólnych wspomnień lub ulubionych kwiatów bliskiej osoby. Wybierz bukiet na rocznicę i zaplanuj jego wręczenie albo doręczenie w Lublinie.",
    ROMANTIC_SET,
  ),

  'przeprosiny': okazja(
    'przeprosiny', 'Przeprosiny',
    "Kwiaty na przeprosiny", "- bukiety z dostawą",
    "Osobisty bukiet może towarzyszyć szczerej rozmowie i przeprosinom. Wybierz kompozycję bliską upodobaniom odbiorcy oraz sposób wręczenia, który uszanuje jego komfort.",
    ROMANTIC_SET,
  ),

  'gratulacje': okazja(
    'gratulacje', 'Gratulacje',
    "Kwiaty na gratulacje", "z dostawą",
    "Awans, nowa praca czy ukończenie ważnego projektu - dobierz kwiaty do sukcesu, który chcecie świętować. Rozważ prezent od siebie albo wspólną kompozycję od zespołu.",
    CELEBRATION_SET,
    GOLD,
  ),

  'podziekowania': okazja(
    'podziekowania', 'Podziękowania',
    "Kwiaty na podziękowanie", "- bukiety i kompozycje",
    "Podziękuj za pomoc, gościnę lub współpracę. Zestaw wybrane kwiaty z krótką wiadomością, która powie, za co jesteś wdzięczny.",
    PASTEL_SET,
    GOLD,
  ),

  'niespodzianka': okazja(
    'niespodzianka', 'Niespodzianka',
    "Kwiaty bez okazji", "- bukiet na niespodziankę",
    "Nie musisz czekać na datę w kalendarzu. Wybierz kwiaty, które kojarzą Ci się z bliską osobą, i zaplanuj drobny gest na zwykły dzień.",
    [
      { img: img1,        pos: 'center top', label: 'Bukiety'    },
      { img: imgPastelowa,pos: 'center',     label: 'Pastelowe'  },
      { img: imgKompozycja,pos:'center',     label: 'Sezonowe'   },
      { img: imgHero,     pos: 'center top', label: 'Premium'    },
    ],
  ),

  'komunia': okazja(
    'komunia', 'Komunia',
    "Kwiaty na komunię", "- bukiety i kompozycje",
    "Spokojne kolory i forma dopasowana do uroczystości. Wybierz kwiatowy upominek lub kompozycję do postawienia, uwzględniając plan spotkania i życzenia rodziny.",
    PASTEL_SET,
  ),

  'obrona': okazja(
    'obrona', 'Obrona pracy',
    "Kwiaty na obronę", "pracy dyplomowej",
    "Pogratuluj ukończenia studiów bukietem do wręczenia po obronie. Przy wyborze uwzględnij wygodę przenoszenia kwiatów i dalsze plany absolwenta.",
    SEASONAL_SET,
    GOLD,
  ),

  'zareczynny': okazja(
    'zareczyny', 'Zaręczyny',
    "Kwiaty", "na zaręczyny",
    "Dobierz bukiet do osoby, której chcesz się oświadczyć, i miejsca zaręczyn. Czerwienie, pastele lub mieszane kwiaty mogą stać się osobistym nawiązaniem do Waszej historii.",
    ROMANTIC_SET,
  ),

  'kwiaty-dla-pary-mlodej': okazja(
    'kwiaty-dla-pary-mlodej', 'Dla Pary Młodej',
    "Kwiaty dla pary młodej", "- bukiety w prezencie ślubnym",
    "Wybierasz się na ślub jako gość? Znajdź bukiet do wręczenia nowożeńcom, biorąc pod uwagę ich życzenia oraz sposób przekazania prezentów po ceremonii.",
    ROMANTIC_SET,
  ),

  'dla-mamy': okazja(
    'dla-mamy', 'Dla Mamy',
    "Kwiaty dla mamy", "- bukiety z dostawą",
    "Na urodziny, z podziękowaniem albo podczas zwykłej wizyty - zacznij od kwiatów i kolorów, które mama lubi. Porównaj formy prezentu pasujące do jej domu.",
    PASTEL_SET,
  ),

  'dla-zony': okazja(
    'dla-zony', 'Dla Żony',
    "Kwiaty dla żony", "- romantyczne bukiety i kompozycje",
    "Przypomnij o wspólnej chwili lub wybierz kwiaty, które żona szczególnie lubi. Znajdź kompozycję na Waszą rocznicę, jej urodziny albo niespodziankę bez okazji.",
    ROMANTIC_SET,
  ),

  'dla-dziewczyny': okazja(
    'dla-dziewczyny', 'Dla Dziewczyny',
    "Kwiaty dla dziewczyny", "- bukiety na wyjątkowe chwile",
    "Delikatny bukiet na spotkanie czy wyrazista kompozycja na urodziny? Kieruj się jej gustem i dopasuj wielkość prezentu do sposobu wręczenia.",
    [
      { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'  },
      { img: imgRozany,    pos: 'center top',label: 'Romantyczne'},
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
    ],
  ),

  'dla-babci': okazja(
    'dla-babci', 'Dla Babci',
    "Kwiaty dla babci", "- bukiety i rośliny na prezent",
    "Pomyśl o ulubionych kwiatach babci i o tym, gdzie najchętniej je ustawia. Wybierz prezent do wazonu lub roślinę, którą z przyjemnością będzie się zajmować.",
    [
      { img: imgPastelowa, pos: 'center',    label: 'Delikatne'  },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'   },
    ],
  ),

  'dla-mezczyzny': okazja(
    'dla-mezczyzny', 'Dla Mężczyzny',
    "Kwiaty dla mężczyzny", "- eleganckie kompozycje z dostawą",
    "Kolor i forma zależą od gustu odbiorcy. Wybierz kwiaty na urodziny, z gratulacjami lub podziękowaniem, bez sztywnych reguł dotyczących „męskiego” bukietu.",
    [
      { img: img3,         pos: 'center top',label: 'Premium'    },
      { img: img2,         pos: 'center',    label: 'Kolorowe'   },
      { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'   },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
    ],
  ),

  'dla-przyjaciela': okazja(
    'dla-przyjaciela', 'Dla Przyjaciela',
    "Kwiaty dla przyjaciela", "- bukiety na wspólne okazje",
    "Kolorowy prezent na urodziny, gratulacje po sukcesie albo mała niespodzianka. Wybierz kwiaty, które pasują do Waszej znajomości, i dołącz kilka osobistych słów.",
    CELEBRATION_SET,
    GOLD,
  ),

  'dzien-babci-i-dziadka': okazja(
    'dzien-babci-i-dziadka', 'Dzień Babci i Dziadka',
    "Kwiaty na Dzień", "Babci i Dziadka",
    "Zaplanuj styczniowe życzenia dla babci, dziadka lub obojga. Możesz wybrać wspólny kwiatowy prezent albo kompozycje dopasowane do ich różnych upodobań.",
    [
      { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'   },
      { img: imgPastelowa, pos: 'center',    label: 'Delikatne'  },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
    ],
    GOLD,
  ),

  'walentynki': okazja(
    'walentynki', 'Walentynki',
    "Kwiaty na Walentynki", "z dostawą w Lublinie",
    "Czerwone róże lub kompozycja w ulubionych kolorach bliskiej osoby. Wybierz kwiaty na 14 lutego, przygotuj bilecik i sprawdź dostępny termin doręczenia.",
    ROSE_SET,
  ),

  'dzien-kobiet': okazja(
    'dzien-kobiet', 'Dzień Kobiet',
    "Kwiaty na Dzień Kobiet", "z dostawą",
    "Wybierz bukiet na 8 marca dla bliskiej osoby albo zaplanuj kwiaty dla kilku kobiet w zespole. Porównaj formę, kolorystykę i koszt wybranych propozycji.",
    [
      { img: imgRoze,      pos: 'center',    label: 'Róże'       },
      { img: imgPastelowa, pos: 'center',    label: 'Pastelowe'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: imgRozany,    pos: 'center top',label: 'Klasyczne'  },
    ],
  ),

  'wielkanoc': okazja(
    'wielkanoc', 'Wielkanoc',
    "Kwiaty i dekoracje", "na Wielkanoc",
    "Kwiatowy akcent na świąteczny stół lub prezent dla gospodarzy. Dobierz kompozycję do miejsca ustawienia, kolorów wnętrza i charakteru rodzinnego spotkania.",
    [
      { img: imgKompozycja,pos: 'center',    label: 'Wiosenne'   },
      { img: img1,         pos: 'center top',label: 'Pastelowe'  },
      { img: imgPastelowa, pos: 'center',    label: 'Delikatne'  },
      { img: img2,         pos: 'center',    label: 'Kolorowe'   },
    ],
    GREEN,
  ),

  'dzien-matki': okazja(
    'dzien-matki', 'Dzień Matki',
    "Kwiaty na Dzień Matki", "z dostawą",
    "Na 26 maja wybierz coś w stylu mamy: ulubione kolory, znajomy gatunek lub inną formę kwiatowego prezentu. Zaplanuj życzenia i sprawdź dostępny termin zamówienia.",
    [
      { img: imgPastelowa, pos: 'center',    label: 'Delikatne'  },
      { img: imgRozany,    pos: 'center top',label: 'Klasyczne'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: imgRoze,      pos: 'center',    label: 'Róże'       },
    ],
  ),

  'dzien-dziecka': okazja(
    'dzien-dziecka', 'Dzień Dziecka',
    "Kwiaty i kolorowe kompozycje", "na Dzień Dziecka",
    "Kwiaty mogą być dodatkiem do świętowania 1 czerwca. Uzgodnij prezent z opiekunem i dopasuj go do wieku oraz zainteresowań obdarowanego dziecka.",
    [
      { img: imgPastelowa, pos: 'center',    label: 'Kolorowe'   },
      { img: img1,         pos: 'center top',label: 'Wiosenne'   },
      { img: img2,         pos: 'center',    label: 'Słoneczne'  },
      { img: imgHero,      pos: 'center top',label: 'Radosne'    },
    ],
    GOLD,
  ),

  'dzien-ojca': okazja(
    'dzien-ojca', 'Dzień Ojca',
    "Kwiaty", "na Dzień Ojca",
    "Podziękuj tacie za wspólny czas i wsparcie. Wybierz na 23 czerwca kwiaty zgodne z jego gustem albo roślinę do miejsca, o które lubi dbać.",
    [
      { img: img3,         pos: 'center top',label: 'Premium'    },
      { img: img2,         pos: 'center',    label: 'Kolorowe'   },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
      { img: imgKompozycja,pos: 'center',    label: 'Sezonowe'   },
    ],
    GOLD,
  ),

  'wszystkich-swietych': okazja(
    'wszystkich-swietych', 'Wszystkich Świętych',
    "Kompozycje kwiatowe", "na Wszystkich Świętych",
    "Wybierz kwiaty na grób bliskiej osoby. Porównaj sposób wykonania i wielkość dekoracji, a przed 1 listopada ustal termin przygotowania lub odbioru.",
    [
      { img: img3,         pos: 'center top',label: 'Dostojne'   },
      { img: imgKompozycja,pos: 'center',    label: 'Klasyczne'  },
      { img: img1,         pos: 'center top',label: 'Tradycyjne' },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
    ],
    '#6B4C8E',
  ),

  'boze-narodzenie': okazja(
    'boze-narodzenie', 'Boże Narodzenie',
    "Kwiaty i dekoracje", "na Boże Narodzenie",
    "Dobierz kwiaty do świątecznej aranżacji domu albo podaruj je gospodarzom spotkania. Przy wyborze kompozycji uwzględnij miejsce na stole oraz barwy dekoracji.",
    [
      { img: imgKompozycja,pos: 'center',    label: 'Świąteczne' },
      { img: img1,         pos: 'center top',label: 'Klasyczne'  },
      { img: imgHero,      pos: 'center top',label: 'Eleganckie' },
      { img: img3,         pos: 'center top',label: 'Premium'    },
    ],
    GREEN,
  ),
};
